import { com_adswizz_synchro_decorateUrl, com_adswizz_synchro_decoratePlaylistUrl } from 'global';
import MetadataParser from './adswizz/metadata-parser';
import MetadataPoller from './adswizz/metadata-poller';
import EventEmitter from '../../utils/event-emitter';
import {
  METADATA_TYPE_ADSWIZZ_STREAM,
  METADATA_TYPE_ADSWIZZ_PODCAST,
  METADATA_TYPE_ADSWIZZ_AD
} from '../constants';
import { url_query_get, url_query_set } from '../../utils/url';

const MIN_INTERVAL = 1;
const MAX_INTERVAL = 60;

const AdswizzTypes = [
  METADATA_TYPE_ADSWIZZ_STREAM,
  METADATA_TYPE_ADSWIZZ_PODCAST,
  METADATA_TYPE_ADSWIZZ_AD
];

/**
 * AdsWizz plugin providing AdsWizz integration
 *
 * @param {object} content Content player
 * @param {object} adverts Advertising player
 * @param {object} options Options to init adswizz plugin
 */
export default class AdsWizz extends EventEmitter {
  static get defaultOptions () {
    return {
      interval: 5,
      metadataUrl: null,
      afrUrl: null,
      zoneAlias: null
    };
  }

  constructor (options) {
    super();

    options = Object.assign({}, AdsWizz.defaultOptions, options);

    if (!options.timeline) {
      throw new Error('Cannot create an AdsWizz instance without supplying a timeline controller');
    }

    if (options.interval < MIN_INTERVAL || options.interval > MAX_INTERVAL) {
      throw new Error(`Invalid AdsWizz interval '${options.interval}', expected a value between ${MIN_INTERVAL} and ${MAX_INTERVAL}`);
    }

    this._timeline = options.timeline;
    this._logger = options.logger;
    this._player = options.player;
    this._zoneAlias = options.zoneAlias;
    this._poller = null;
    this._parser = null;

    this._parser = new MetadataParser({
      afrUrl: options.afrUrl,
      zoneAlias: options.zoneAlias
    });

    if (options.gdpr) {
      this.setGDPR(options.gdpr);
    }

    if (options.metadataUrl) {
      this._poller = new MetadataPoller({
        url: options.metadataUrl,
        interval: options.interval * 1000,
        withCredentials: true,
        logger: options.logger,
        parser: this._parser
      });

      // subscribe to the poller for updates
      this._poller.subscribe(this._pollerHandler, this);

      // bind playback events
      this._player.on('playing', this.startPolling, { context: this });
      this._player.on('pause', this.stopPolling, { context: this });
      this._player.on('stop', this.stopPolling, { context: this });
      this._player.on('error', this.stopPolling, { context: this });
    }

    this._player.on('playlistselect', this.decoratePlaylistItem, { context: this });
  }

  /**
   * Decorate a playlist item from a playlistselect event with AdsWizz parameters
   *
   * @param  {Object} event A playlistselect event
   */
  decoratePlaylistItem (event) {
    const item = event.playlistItem;
    if (AdswizzTypes.includes(item.metadata.contentType)) {
      item.audio = item.audio.map(audio => {
        audio.url = this.decorateUrl(audio.url);
        return audio;
      });
    }
  }

  /**
   * Decorate a playback url with AdsWizz parameters
   *
   * @param  {string} url The playback url to decorate
   * @return {string}     The decroated playback url
   */
  decorateUrl (url) {
    // ensure the AdsWizz decorater has been loaded
    if (typeof com_adswizz_synchro_decorateUrl !== 'function') {
      return url;
    }

    let adswizzUrl;

    // ensure we don't double-decorate the url
    if (url_query_get(url, 'listenerid')) {
      adswizzUrl = url;
    } else if (!this._zoneAlias) {
      adswizzUrl = com_adswizz_synchro_decoratePlaylistUrl(url);
    } else {
      adswizzUrl = com_adswizz_synchro_decorateUrl(url);
    }

    // add the aw_0_1st.ri parameter - used by SharpStream to map adswizz podcasts to analytics data
    adswizzUrl = url_query_set(adswizzUrl, 'aw_0_1st.ri', 'iono');

    return adswizzUrl;
  }

  /**
   * Update the GDPR configuration used by AdsWizz
   *
   * @param {boolean|object} gdpr
   */
  setGDPR (gdpr) {
    this._parser.setGDPR(gdpr);
  }

  /**
   * Start polling the AdsWizz metadata stream
   */
  startPolling () {
    const playlistItem = this._player.playlist.getPlaylistItem();

    if (this._poller && playlistItem.metadata.contentType === METADATA_TYPE_ADSWIZZ_STREAM && !this._poller._started) {
      this._poller.start();
      this._logger.debug('Started polling the AdsWizz metadata stream');
    }
  }

  /**
   * Stop polling the AdsWizz metadata stream
   */
  stopPolling () {
    const playlistItem = this._player.playlist.getPlaylistItem();

    if (this._poller && playlistItem && playlistItem.metadata.contentType === METADATA_TYPE_ADSWIZZ_STREAM) {
      this._poller.stop();
      this._logger.debug('Stopped polling the AdsWizz metadata stream');
    }
  }

  /**
   * Handler for polling responses
   *
   * Parses a metadata list from the polling response and inserts entries on to the timeline
   *
   * @todo handle errrors
   * @param {Error} err
   * @param {Array} entries
   */
  _pollerHandler (err, entries) {
    if (err) {
      this._logger.warn(`AdsWizz: failed to poll for metadata - ${err.message}`);
    } else {
      entries.forEach(entry => {
        this._timeline.insert(entry);
        if (entry.metadata && entry.metadata.adswizzContext) {
          this._logger.debug(`AdsWizz: new adswizzContext '${entry.metadata.adswizzContext}' received at ${this._player.getPosition()} for ad '${entry.metadata.uid}' starting at position ${entry.startTime}`);
        }
      });
    }
  }
}
